import type { FC } from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { UserButton } from '@clerk/clerk-react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ROUTES } from 'paths';

export const SIDE_NAV_WIDTH = 256;
export const SIDE_NAV_COLLAPSED_WIDTH = 80;

const navigation = [
  {
    section: 'OVERVIEW',
    items: [
      { title: 'Instructions', path: ROUTES.INSTRUCTIONS, icon: AssignmentIcon },
      { title: 'Calendar', path: ROUTES.CALENDAR, icon: CalendarMonthIcon },
    ],
  },
  {
    section: 'MANAGEMENT',
    items: [
      { title: 'Agents', path: ROUTES.AGENTS, icon: SmartToyIcon },
      { title: 'Chat', path: ROUTES.CHAT, icon: ChatIcon },
    ],
  },
  {
    section: 'ANALYTICS',
    items: [
      { title: 'Analytics', path: ROUTES.ANALYTICS, icon: BarChartIcon },
      { title: 'Quiz Reports', path: ROUTES.REPORTS, icon: AssessmentIcon },
    ],
  },
  {
    section: 'SETTINGS',
    items: [
      { title: 'API Keys', path: ROUTES.API_KEYS, icon: VpnKeyIcon },
    ],
  },
];

interface SideNavProps {
  sections?: any[];
}

const Logo = styled('div')`
  display: flex;
  align-items: center;
`;

const LogoIcon = styled(SmartToyIcon)`
  color: #0099CC;
  font-size: 32px;
`;

export const SideNav: FC<SideNavProps> = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const sideNavWidth = isCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;

  return (
    <Drawer
      anchor="left"
      open
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#F0F9FC',
            borderRight: '1px solid #E5E7EB',
            width: sideNavWidth,
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          },
        },
      }}
      variant="permanent"
    >
      <Stack sx={{ height: '100%' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            height: 64,
            px: 2,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          {!isCollapsed && (
            <Logo>
              <LogoIcon />
              <Typography
                variant="h6"
                sx={{
                  ml: 1,
                  fontWeight: 700,
                  color: '#2C3E50',
                  fontSize: '1.1rem',
                }}
              >
                CX<span style={{ color: '#0099CC' }}>Cortex</span>
                <span style={{ color: '#6B7280', fontWeight: 400 }}> - ATS</span>
              </Typography>
            </Logo>
          )}
          {isCollapsed && (
            <Box sx={{ mx: 'auto' }}>
              <LogoIcon />
            </Box>
          )}
          <IconButton
            onClick={() => setIsCollapsed(!isCollapsed)}
            sx={{
              p: 0.75,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#E5E7EB',
              },
              ...(isCollapsed && {
                mx: 'auto',
                mt: 1,
              }),
            }}
          >
            {isCollapsed ? (
              <ChevronRightIcon sx={{ color: '#6B7280' }} />
            ) : (
              <ChevronLeftIcon sx={{ color: '#6B7280' }} />
            )}
          </IconButton>
        </Stack>

        {/* Navigation */}
        <Stack
          component="nav"
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 1.5,
            py: 2,
          }}
          spacing={3}
        >
          {navigation.map((section) => (
            <Box key={section.section}>
              {!isCollapsed && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    mb: 1,
                    display: 'block',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#9CA3AF',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  {section.section}
                </Typography>
              )}
              <Stack spacing={0.5}>
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;

                  const button = (
                    <ButtonBase
                      key={item.title}
                      onClick={() => navigate(item.path)}
                      sx={{
                        width: '100%',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        alignItems: 'center',
                        px: isCollapsed ? 1 : 1.5,
                        py: 1.25,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        ...(isActive
                          ? {
                              backgroundColor: '#0099CC',
                              color: '#FFFFFF',
                              boxShadow: '0 2px 4px rgba(0, 153, 204, 0.3)',
                              '& svg': {
                                color: '#FFFFFF',
                              },
                            }
                          : {
                              color: '#374151',
                              '&:hover': {
                                backgroundColor: '#E5E7EB',
                                '& svg': {
                                  color: '#0099CC',
                                },
                              },
                              '& svg': {
                                color: '#6B7280',
                              },
                            }),
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 20,
                          flexShrink: 0,
                          mr: isCollapsed ? 0 : 1.5,
                        }}
                      />
                      {!isCollapsed && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          {item.title}
                        </Typography>
                      )}
                    </ButtonBase>
                  );

                  return isCollapsed ? (
                    <Tooltip key={item.title} title={item.title} placement="right">
                      {button}
                    </Tooltip>
                  ) : (
                    button
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Bottom Section */}
        <Stack
          sx={{
            borderTop: '1px solid #E5E7EB',
            p: 2,
          }}
          spacing={1}
        >
          <Tooltip title={isCollapsed ? 'Help & Support' : ''} placement="right">
            <ButtonBase
              onClick={() => navigate(ROUTES.SUPPORT)}
              sx={{
                width: '100%',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                alignItems: 'center',
                px: isCollapsed ? 1 : 1.5,
                py: 1.25,
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                ...(location.pathname === ROUTES.SUPPORT
                  ? {
                      backgroundColor: '#0099CC',
                      color: '#FFFFFF',
                      boxShadow: '0 2px 4px rgba(0, 153, 204, 0.3)',
                      '& svg': {
                        color: '#FFFFFF',
                      },
                    }
                  : {
                      color: '#374151',
                      '&:hover': {
                        backgroundColor: '#E5E7EB',
                        '& svg': {
                          color: '#0099CC',
                        },
                      },
                      '& svg': {
                        color: '#6B7280',
                      },
                    }),
              }}
            >
              <HelpOutlineIcon
                sx={{
                  fontSize: 20,
                  mr: isCollapsed ? 0 : 1.5,
                }}
              />
              {!isCollapsed && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Help & Support
                </Typography>
              )}
            </ButtonBase>
          </Tooltip>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: isCollapsed ? 0 : 1.5,
              py: 1,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
          >
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: {
                    height: 32,
                    width: 32,
                  },
                },
              }}
            />
            {!isCollapsed && (
              <Typography
                variant="body2"
                sx={{
                  ml: 1.5,
                  color: '#6B7280',
                  fontWeight: 500,
                }}
              >
                Account
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>
    </Drawer>
  );
};
